import { type RefObject, useEffect, useRef } from 'react'

/** The `--ease-out` token (app.css); the Web Animations API cannot read a CSS variable. */
const EASE_OUT = 'cubic-bezier(0.2, 0.7, 0.3, 1)'

/**
 * A page arriving: the content fades in and settles 4px up, 180ms, each time `pageKey` changes
 * (not on first load). It animates the element itself rather than a wrapper, so pages that fill the
 * main area (`h-full`) keep their layout. Nothing moves when the user asks for reduced motion.
 */
export function usePageEnterMotion(ref: RefObject<HTMLElement | null>, pageKey: string | undefined) {
  const previous = useRef(pageKey)
  useEffect(() => {
    if (pageKey === previous.current) return
    previous.current = pageKey
    const el = ref.current
    if (!el || typeof el.animate !== 'function') return
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return
    const animation = el.animate(
      [
        { opacity: 0, transform: 'translateY(4px)' },
        { opacity: 1, transform: 'none' },
      ],
      { duration: 180, easing: EASE_OUT },
    )
    return () => animation.cancel()
  }, [ref, pageKey])
}
